import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchAuthSession,
  getCurrentUser,
  signOut as amplifySignOut,
  type AuthUser,
} from 'aws-amplify/auth';

interface AuthState {
  user: AuthUser | null;
  isAdmin: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  async function loadSession() {
    try {
      const [currentUser, session] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession(),
      ]);
      setUser(currentUser);

      const groups =
        (session.tokens?.accessToken?.payload?.['cognito:groups'] as string[]) ??
        [];
      setIsAdmin(groups.includes('admin'));
    } catch {
      setUser(null);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadSession();
  }, []);

  async function signOut() {
    await amplifySignOut();
    setUser(null);
    setIsAdmin(false);
  }

  return (
    <AuthContext.Provider
      value={{ user, isAdmin, isLoading, signOut, refreshSession: loadSession }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
